/**
 * Cashier mock API (B1.7) — deterministic fixture path.
 *
 * This module is the MOCK leg of the mock↔live dispatcher at `./cashier-api`.
 * Live (HTTP) implementation lives in `./cashier-api-http`; the dispatcher
 * picks one at module load based on `APP_ENV.bizApiMode`.
 *
 * Purpose:
 *   - Deterministic seed rows for Lovable previews / Storybook / pre-wire
 *     sessions (no backend dependency).
 *   - End-to-end UX contract parity: every mutation returns a fresh
 *     `CashierSnapshot` so React Query cache updates are a single
 *     `setQueryData(snapshot)` call.
 *
 * Auth/permission notes (enforced server-side on the live path):
 *   - `commitTender` requires role: cashier | manager | owner
 *   - `closeSession` requires role: manager | owner
 *   - `applyDiscount` with `kind='manager_override'` requires manager pin
 *
 * DO NOT import from this file directly outside the feature. Consumers MUST
 * go through `./cashier-api` so the mock↔live swap is transparent.
 *
 * @see cashier-api.ts (dispatcher)
 * @see cashier-api-http.ts (live path)
 */

import {
  CASHIER_ACTIVE_ORDER_SEED,
  CASHIER_AVAILABLE_TENDERS_SEED,
  CASHIER_CATALOG_SEED,
  CASHIER_CATEGORIES_SEED,
  CASHIER_PARKED_ORDERS_SEED,
  CASHIER_SESSION_SEED,
  computeOrderTotals,
} from './seeds';
import type {
  AddCartItemRequest,
  ApplyDiscountRequest,
  CashierOrder,
  CashierOrderLine,
  CashierSnapshot,
  CloseSessionRequest,
  CommitTenderRequest,
  CommitTenderResult,
  GetCashierSnapshotParams,
  ParkOrderRequest,
  RemoveCartItemRequest,
  ResumeParkedRequest,
  UpdateCartQtyRequest,
  VoidOrderRequest,
} from './types';

const MOCK_LATENCY_MS = 120;

/**
 * In-memory store — simulates backend state between calls.
 * Reset by reload (intentional: Lovable preview refresh should reset demo).
 */
interface CashierStore {
  branchId: string;
  branchName: string;
  session: CashierSnapshot['session'];
  activeOrder: CashierOrder;
  parkedOrders: CashierOrder[];
}

const store: CashierStore = {
  branchId: '',
  branchName: 'Merkez Şube',
  session: { ...CASHIER_SESSION_SEED },
  activeOrder: cloneOrder(CASHIER_ACTIVE_ORDER_SEED),
  parkedOrders: CASHIER_PARKED_ORDERS_SEED.map(cloneOrder),
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function cloneOrder(order: CashierOrder): CashierOrder {
  return {
    ...order,
    lines: order.lines.map((l) => ({ ...l })),
    tenders: order.tenders.map((t) => ({ ...t })),
  };
}

function buildSnapshot(): CashierSnapshot {
  return {
    branchId: store.branchId,
    branchName: store.branchName,
    session: store.session,
    activeOrder: cloneOrder(store.activeOrder),
    parkedOrders: store.parkedOrders.map(cloneOrder),
    categories: CASHIER_CATEGORIES_SEED,
    catalog: CASHIER_CATALOG_SEED,
    availableTenders: CASHIER_AVAILABLE_TENDERS_SEED,
    updatedAt: new Date().toISOString(),
  };
}

function recomputeActiveOrder(lines: CashierOrderLine[]): CashierOrder {
  const totals = computeOrderTotals(lines);
  return {
    ...store.activeOrder,
    lines,
    ...totals,
  };
}

// ============================================================================
// Read
// ============================================================================

export async function getCashierSnapshot(
  params: GetCashierSnapshotParams,
): Promise<CashierSnapshot> {
  await sleep(MOCK_LATENCY_MS);
  store.branchId = params.branchId;
  return buildSnapshot();
}

/**
 * Mock-only appointment bridge used by `appointments-api-mock`.
 *
 * Live persistence is owned by `/biz/appointments/:id/cashier-order`; the mock
 * path mirrors that contract so browser/UI smoke can prove the same handoff
 * without a backend or real provider credentials.
 */
export function attachAppointmentOrderToCashierMock(order: CashierOrder): CashierSnapshot {
  const existingActive = store.activeOrder.id === order.id;
  const existingParked = store.parkedOrders.find((item) => item.id === order.id);
  if (existingActive || existingParked) {
    if (existingParked) {
      store.parkedOrders = store.parkedOrders.filter((item) => item.id !== order.id);
      store.activeOrder = { ...cloneOrder(existingParked), status: 'open', parkedLabel: null };
    }
    return buildSnapshot();
  }

  if (store.activeOrder.lines.length > 0) {
    store.parkedOrders = [
      {
        ...cloneOrder(store.activeOrder),
        status: 'parked',
        parkedLabel: store.activeOrder.parkedLabel ?? 'Bekleyen Sipariş',
      },
      ...store.parkedOrders,
    ];
  }

  store.activeOrder = {
    ...cloneOrder(order),
    sessionId: store.session.id,
    status: 'open',
    parkedLabel: null,
  };

  return buildSnapshot();
}

// ============================================================================
// Cart mutations
// ============================================================================

export async function addCartItem(
  req: AddCartItemRequest,
): Promise<CashierSnapshot> {
  await sleep(80);
  const catalogItem = CASHIER_CATALOG_SEED.find((i) => i.id === req.itemId);
  if (!catalogItem) {
    throw new Error(`catalog item not found: ${req.itemId}`);
  }

  const qty = Math.max(1, req.qty);
  const existingLine = store.activeOrder.lines.find(
    (l) => l.itemId === req.itemId && !l.discount && !l.note,
  );

  let nextLines: CashierOrderLine[];
  if (existingLine) {
    nextLines = store.activeOrder.lines.map((l) =>
      l.lineId === existingLine.lineId
        ? {
            ...l,
            qty: l.qty + qty,
            lineTotalMinor: (l.qty + qty) * l.unitPriceMinor,
          }
        : l,
    );
  } else {
    const newLine: CashierOrderLine = {
      lineId: `line_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`,
      itemId: catalogItem.id,
      label: catalogItem.label,
      qty,
      unitPriceMinor: catalogItem.priceMinor,
      lineTotalMinor: qty * catalogItem.priceMinor,
      taxRate: catalogItem.taxRate,
      discount: null,
      note: req.note ?? null,
    };
    nextLines = [...store.activeOrder.lines, newLine];
  }

  store.activeOrder = recomputeActiveOrder(nextLines);
  return buildSnapshot();
}

export async function updateCartQty(
  req: UpdateCartQtyRequest,
): Promise<CashierSnapshot> {
  await sleep(60);
  const nextLines = store.activeOrder.lines
    .map((l) =>
      l.lineId === req.lineId
        ? {
            ...l,
            qty: Math.max(0, req.qty),
            lineTotalMinor:
              Math.max(0, req.qty) * l.unitPriceMinor -
              (l.discount?.valueMinor ?? 0),
          }
        : l,
    )
    .filter((l) => l.qty > 0);
  store.activeOrder = recomputeActiveOrder(nextLines);
  return buildSnapshot();
}

export async function removeCartItem(
  req: RemoveCartItemRequest,
): Promise<CashierSnapshot> {
  await sleep(60);
  const nextLines = store.activeOrder.lines.filter(
    (l) => l.lineId !== req.lineId,
  );
  store.activeOrder = recomputeActiveOrder(nextLines);
  return buildSnapshot();
}

export async function applyDiscount(
  req: ApplyDiscountRequest,
): Promise<CashierSnapshot> {
  await sleep(80);
  if (req.lineId === null) {
    // Order-level discount applied as proportional line discount.
    const subtotal = store.activeOrder.lines.reduce(
      (acc, l) => acc + l.qty * l.unitPriceMinor,
      0,
    );
    if (subtotal === 0) return buildSnapshot();
    const totalDiscount =
      req.kind === 'percent'
        ? Math.round(subtotal * (req.valueMinor / 100))
        : req.valueMinor;
    const nextLines = store.activeOrder.lines.map((l) => {
      const share = Math.round(
        ((l.qty * l.unitPriceMinor) / subtotal) * totalDiscount,
      );
      return {
        ...l,
        discount: {
          kind: req.kind,
          valueMinor: share,
          reasonCode: req.reasonCode,
        },
        lineTotalMinor: l.qty * l.unitPriceMinor - share,
      };
    });
    store.activeOrder = recomputeActiveOrder(nextLines);
  } else {
    const nextLines = store.activeOrder.lines.map((l) => {
      if (l.lineId !== req.lineId) return l;
      const base = l.qty * l.unitPriceMinor;
      const discountValue =
        req.kind === 'percent'
          ? Math.round(base * (req.valueMinor / 100))
          : req.valueMinor;
      return {
        ...l,
        discount: {
          kind: req.kind,
          valueMinor: discountValue,
          reasonCode: req.reasonCode,
        },
        lineTotalMinor: base - discountValue,
      };
    });
    store.activeOrder = recomputeActiveOrder(nextLines);
  }
  return buildSnapshot();
}

// ============================================================================
// Order lifecycle
// ============================================================================

export async function parkOrder(
  req: ParkOrderRequest,
): Promise<CashierSnapshot> {
  await sleep(100);
  if (store.activeOrder.lines.length === 0) {
    return buildSnapshot();
  }
  const parked: CashierOrder = {
    ...cloneOrder(store.activeOrder),
    status: 'parked',
    parkedLabel: req.label,
  };
  store.parkedOrders = [parked, ...store.parkedOrders];
  store.activeOrder = buildEmptyActiveOrder();
  return buildSnapshot();
}

export async function resumeParked(
  req: ResumeParkedRequest,
): Promise<CashierSnapshot> {
  await sleep(100);
  const parked = store.parkedOrders.find((o) => o.id === req.orderId);
  if (!parked) {
    throw new Error(`parked order not found: ${req.orderId}`);
  }

  // If current active has lines, swap into parked list under a default label.
  const currentHasLines = store.activeOrder.lines.length > 0;
  const nextParkedList = store.parkedOrders.filter((o) => o.id !== req.orderId);
  if (currentHasLines) {
    nextParkedList.unshift({
      ...cloneOrder(store.activeOrder),
      status: 'parked',
      parkedLabel: store.activeOrder.parkedLabel ?? 'Bekleyen Sipariş',
    });
  }
  store.parkedOrders = nextParkedList;
  store.activeOrder = { ...cloneOrder(parked), status: 'open', parkedLabel: null };
  return buildSnapshot();
}

export async function voidOrder(_req: VoidOrderRequest): Promise<CashierSnapshot> {
  await sleep(80);
  store.activeOrder = buildEmptyActiveOrder();
  return buildSnapshot();
}

export async function commitTender(
  req: CommitTenderRequest,
): Promise<CommitTenderResult> {
  await sleep(140);
  const totalPaid = req.tenders.reduce((acc, t) => acc + t.amountMinor, 0);
  if (totalPaid < store.activeOrder.totalMinor) {
    throw new Error('tender.insufficient');
  }

  const committedOrder: CashierOrder = {
    ...store.activeOrder,
    tenders: req.tenders.map((t) => ({ ...t })),
    status: 'paid',
    closedAt: new Date().toISOString(),
  };

  // Roll session GMV forward.
  store.session = {
    ...store.session,
    ordersCount: store.session.ordersCount + 1,
    grossMinor: store.session.grossMinor + committedOrder.totalMinor,
    cashCollectedMinor:
      store.session.cashCollectedMinor +
      req.tenders
        .filter((t) => t.method === 'cash')
        .reduce((acc, t) => acc + t.amountMinor, 0),
  };
  store.activeOrder = buildEmptyActiveOrder();

  return {
    order: committedOrder,
    receiptId: `rcpt_${Date.now()}`,
    committedAt: new Date().toISOString(),
  };
}

export async function closeSession(
  _req: CloseSessionRequest,
): Promise<CashierSnapshot> {
  await sleep(160);
  if (store.session.status === 'closed' || store.session.status === 'reconciled') {
    // Idempotent: silently return current state.
    return buildSnapshot();
  }
  store.session = {
    ...store.session,
    status: 'closed',
    closedAt: new Date().toISOString(),
  };
  return buildSnapshot();
}

// ============================================================================
// Helpers
// ============================================================================

function buildEmptyActiveOrder(): CashierOrder {
  return {
    id: `ord_${Date.now()}`,
    sessionId: store.session.id,
    customerId: null,
    customerName: null,
    petId: null,
    petName: null,
    lines: [],
    subtotalMinor: 0,
    orderDiscountMinor: 0,
    taxMinor: 0,
    totalMinor: 0,
    tenders: [],
    status: 'open',
    parkedLabel: null,
    openedAt: new Date().toISOString(),
    closedAt: null,
  };
}
